import { useEffect, useState } from 'react'
import { Plus, Trash2, FileText } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtDate, fmtEuro, hoje } from '../utils/fmt'

const CATEGORIAS = ['AGUA','ELETRICIDADE','LIMPEZA','SEGUROS','MANUTENCAO','ELEVADORES','JARDINS','PORTOES','SEGURANCA','OBRAS','FUNDO_RESERVA','OUTRO']
const CATEGORIA_LABEL = { AGUA:'Água', ELETRICIDADE:'Eletricidade', LIMPEZA:'Limpeza', SEGUROS:'Seguros', MANUTENCAO:'Manutenção', ELEVADORES:'Elevadores', JARDINS:'Jardins', PORTOES:'Portões', SEGURANCA:'Segurança', OBRAS:'Obras', FUNDO_RESERVA:'Fundo Reserva', OUTRO:'Outro' }
const CATEGORIA_COLOR = { AGUA:'blue', ELETRICIDADE:'blue', LIMPEZA:'green', SEGUROS:'gray', MANUTENCAO:'amber', ELEVADORES:'amber', JARDINS:'green', PORTOES:'gray', SEGURANCA:'gray', OBRAS:'red', FUNDO_RESERVA:'blue', OUTRO:'gray' }

const emptyForm = { data: hoje(), fornecedor: '', nifFornecedor: '', categoria: 'LIMPEZA', valor: '', descricao: '', observacoes: '' }

export default function Despesas() {
  const { condominioAtivo } = useAppStore()
  const [despesas, setDespesas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')

  const load = () => {
    if (!condominioAtivo) return
    setLoading(true)
    const params = { condominioId: condominioAtivo.id, ...(filtroCategoria && { categoria: filtroCategoria }) }
    api.get('/despesas', { params }).then(r => setDespesas(r.data.despesas || [])).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [condominioAtivo, filtroCategoria])

  const openCreate = () => { setForm(emptyForm); setEditId(null); setErro(''); setModal(true) }
  const openEdit = (d) => {
    setForm({
      data: new Date(d.data).toISOString().split('T')[0],
      fornecedor: d.fornecedor, nifFornecedor: d.nifFornecedor || '',
      categoria: d.categoria, valor: d.valor, descricao: d.descricao, observacoes: d.observacoes || ''
    })
    setEditId(d.id); setErro(''); setModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      const payload = { ...form, condominioId: condominioAtivo.id, valor: parseFloat(form.valor) }
      if (editId) await api.put(`/despesas/${editId}`, payload)
      else await api.post('/despesas', payload)
      setModal(false); load()
    } catch (err) {
      setErro(err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || 'Erro ao guardar.')
    } finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta despesa?')) return
    await api.delete(`/despesas/${id}`); load()
  }

  const total = despesas.reduce((s, d) => s + d.valor, 0)

  return (
    <>
      <Topbar title="Despesas" actions={
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Nova despesa</button>
      } />
      <div className="page-content">
        <div className="page-header">
          <h1>Despesas</h1>
          <p>{despesas.length} registos · Total: {fmtEuro(total)}</p>
        </div>

        <div className="tabs" style={{ overflowX: 'auto' }}>
          <button className={`tab ${filtroCategoria === '' ? 'active' : ''}`} onClick={() => setFiltroCategoria('')}>Todas</button>
          {CATEGORIAS.map(c => (
            <button key={c} className={`tab ${filtroCategoria === c ? 'active' : ''}`} onClick={() => setFiltroCategoria(c)}>
              {CATEGORIA_LABEL[c]}
            </button>
          ))}
        </div>

        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Data</th><th>Fornecedor</th><th>Categoria</th><th>Valor</th><th>Descrição</th><th></th></tr>
                </thead>
                <tbody>
                  {despesas.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem despesas registadas.</td></tr>
                  ) : despesas.map(d => (
                    <tr key={d.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(d.data)}</td>
                      <td>{d.fornecedor}</td>
                      <td><span className={`badge badge-${CATEGORIA_COLOR[d.categoria] || 'gray'}`}>{CATEGORIA_LABEL[d.categoria] || d.categoria}</span></td>
                      <td style={{ fontWeight: 600 }}>{fmtEuro(d.valor)}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{d.descricao}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {d.documento && <FileText size={14} color="var(--blue)" />}
                          <button className="btn btn-sm" onClick={() => openEdit(d)} title="Editar">✏</button>
                          <button onClick={() => handleDelete(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }} title="Eliminar">
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Editar despesa' : 'Nova despesa'}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-secondary)' }}>×</button>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input className="form-input" type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Valor (€) *</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Fornecedor *</label>
                <input className="form-input" value={form.fornecedor} onChange={e => setForm({ ...form, fornecedor: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">NIF Fornecedor</label>
                  <input className="form-input" value={form.nifFornecedor} onChange={e => setForm({ ...form, nifFornecedor: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Categoria *</label>
                  <select className="form-select" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{CATEGORIA_LABEL[c]}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descrição *</label>
                <input className="form-input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Observações</label>
                <textarea className="form-input" rows={2} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" /> : editId ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
